/**
 * Local calendar dates as `YYYY-MM-DD` strings.
 *
 * Nothing here builds a `Date` from a string or from `Date.now()`. `new Date('2026-03-01')` parses as
 * UTC midnight and then renders in the viewer's zone, so anyone west of Greenwich sees the previous
 * day; `new Date()` carries a time-of-day that makes month arithmetic drift across a DST boundary.
 * Every `Date` in this file is constructed from explicit numeric parts, which is timezone-free, and is
 * converted straight back to parts before it leaves.
 */

/** A local calendar date, `YYYY-MM-DD`. */
export type CalendarDate = string;

/** Which endpoint of a range a click landed on, and the range it produced. */
export interface CalendarRange {
  readonly from?: CalendarDate;
  readonly to?: CalendarDate;
}

export enum MiniCalendarMode {
  Single = 'Single',
  Range = 'Range',
}

/** Year and zero-based month — the unit the grid is built from. */
export interface CalendarMonth {
  readonly year: number;
  readonly month: number;
}

/** One cell of the 7-column grid. A blank leading/trailing cell has no date. */
export interface CalendarCell {
  readonly date: CalendarDate | null;
  readonly day: number | null;
}

/** Monday-first weekday headers, matching the grid's column order. */
export const CALENDAR_WEEKDAYS: readonly string[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

const MONTH_NAMES: readonly string[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Splits a `YYYY-MM-DD` string into numeric parts, or null when it is not one. */
export function parseCalendarDate(value: CalendarDate | null | undefined): CalendarMonth & { day: number } | null {

  if (!value) return null;

  const match = DATE_PATTERN.exec(value);
  if (!match) return null;

  return { year: Number(match[1]), month: Number(match[2]) - 1, day: Number(match[3]) };
}

/** Formats numeric parts as `YYYY-MM-DD`. */
export function toCalendarDate(year: number, month: number, day: number): CalendarDate {

  const paddedMonth = String(month + 1).padStart(2, '0');
  const paddedDay = String(day).padStart(2, '0');

  return `${year}-${paddedMonth}-${paddedDay}`;
}

/** Today in the viewer's own zone. The one place a wall clock is read. */
export function todayCalendarDate(now: Date = new Date()): CalendarDate {

  return toCalendarDate(now.getFullYear(), now.getMonth(), now.getDate());
}

/** Days in a month, leap years included. Feb of a year divisible by 400 or by 4-but-not-100 has 29. */
export function daysInMonth(year: number, month: number): number {

  return new Date(year, month + 1, 0).getDate();
}

/**
 * Column index (0 = Monday) of the first of the month. `getDay()` is Sunday-first, so a Sunday start
 * has to land in column 6, not column 0 — the off-by-one that puts an entire month on the wrong day.
 */
export function leadingBlankCount(year: number, month: number): number {

  const sundayFirst = new Date(year, month, 1).getDay();

  return (sundayFirst + 6) % 7;
}

/**
 * Every grid is exactly six weeks. A month needs four, five or six rows depending on its length and
 * which column it starts in, and letting the row count follow the month makes the calendar — and any
 * dialog around it — change height as the user pages, which reads as the panel jumping. Padding to a
 * constant 42 cells costs at most two blank rows and holds the layout still. Blank cells carry no date
 * and no day number: the template renders them as inert placeholders, never as adjacent-month days.
 */
export const CALENDAR_GRID_CELLS = 42;

/** Leading blanks, then every day of the month, then trailing blanks out to the fixed six-week grid. */
export function buildMonthCells(year: number, month: number): readonly CalendarCell[] {

  const cells: CalendarCell[] = [];
  const leading = leadingBlankCount(year, month);
  const total = daysInMonth(year, month);

  for (let index = 0; index < leading; index++) cells.push({ date: null, day: null });
  for (let day = 1; day <= total; day++) cells.push({ date: toCalendarDate(year, month, day), day });

  while (cells.length < CALENDAR_GRID_CELLS) cells.push({ date: null, day: null });

  return cells;
}

/** Steps the displayed month by whole months, rolling the year over at either end. */
export function shiftMonth(current: CalendarMonth, delta: number): CalendarMonth {

  const absolute = (current.year * 12) + current.month + delta;

  return { year: Math.floor(absolute / 12), month: ((absolute % 12) + 12) % 12 };
}

/** `March 2026` — the header label. */
export function formatMonthLabel(current: CalendarMonth): string {

  return `${MONTH_NAMES[current.month]} ${current.year}`;
}

/** True when `a` falls before `b`. Safe as a plain string compare because the format is fixed-width. */
export function isBefore(a: CalendarDate, b: CalendarDate): boolean {

  return a < b;
}

/** True when `date` sits strictly inside the range — endpoints excluded, so they can paint solid. */
export function isWithinRange(date: CalendarDate, range: CalendarRange): boolean {

  if (!range.from || !range.to) return false;

  return date > range.from && date < range.to;
}

/**
 * Applies a click to a range: first sets `from`, second closes it (swapping when the second click is
 * earlier than the first), and a click on a closed range starts over from that day.
 */
export function applyRangeSelection(current: CalendarRange, date: CalendarDate): CalendarRange {

  if (!current.from || current.to) return { from: date };

  if (isBefore(date, current.from)) return { from: date, to: current.from };

  return { from: current.from, to: date };
}

/**
 * The range a second click would produce if it landed on `hovered`, used to paint the prospective
 * band while the pick is still half-open. Once the range is closed, or nothing is started, there is
 * nothing to preview and the committed range is returned untouched.
 */
export function previewRangeSelection(current: CalendarRange, hovered: CalendarDate | null): CalendarRange {

  if (!hovered || !current.from || current.to) return current;

  return applyRangeSelection(current, hovered);
}

/** Steps a date by whole days. Built from numeric parts, so it crosses months and DST without drifting. */
export function shiftDay(date: CalendarDate, delta: number): CalendarDate {

  const parts = parseCalendarDate(date);
  if (!parts) return date;

  const shifted = new Date(parts.year, parts.month, parts.day + delta);

  return toCalendarDate(shifted.getFullYear(), shifted.getMonth(), shifted.getDate());
}

/** The `months` panels starting at `first` — the headings and grids a multi-month calendar renders. */
export function monthSequence(first: CalendarMonth, months: number): readonly CalendarMonth[] {

  const sequence: CalendarMonth[] = [];
  for (let index = 0; index < months; index++) sequence.push(shiftMonth(first, index));

  return sequence;
}

/** True when `date` falls in one of the displayed panels — the test for whether a keyboard move needs a page turn. */
export function isMonthDisplayed(date: CalendarDate, first: CalendarMonth, months: number): boolean {

  const parts = parseCalendarDate(date);
  if (!parts) return false;

  return monthSequence(first, months).some(month => month.year === parts.year && month.month === parts.month);
}

/**
 * The first panel that should be on screen for `date` given how many panels are shown.
 *
 * A date already visible leaves the panels where they are — the point of a two-month view is that
 * arrowing from the end of the left month into the right one does not turn the page. Only a date
 * outside the window moves it, and then the date lands in the first panel.
 */
export function firstMonthFor(date: CalendarDate, current: CalendarMonth, months: number): CalendarMonth {

  if (isMonthDisplayed(date, current, months)) return current;

  const parts = parseCalendarDate(date);
  if (!parts) return current;

  return { year: parts.year, month: parts.month };
}

/** Grid column (0 = Monday) a date occupies. `getDay()` is Sunday-first, so Sunday belongs in column 6. */
export function weekdayColumn(date: CalendarDate): number {

  const parts = parseCalendarDate(date);
  if (!parts) return 0;

  return (new Date(parts.year, parts.month, parts.day).getDay() + 6) % 7;
}
